import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';

/**
 * Base-formation API client (T-0047 / E03-F01 §4).
 *
 * Three surfaces under `/api/v1/engagements/:engagement_id/base-formation`:
 *   - POST /run                 — manual trigger; honors Idempotency-Key.
 *                                  Returns 202 with `{ run_id, status }`.
 *   - GET  /runs/:run_id        — status + counts for one run.
 *   - GET  /runs/latest         — the newest run as `{ run }` (null when the
 *                                  engagement never ran); the documents
 *                                  screen reads it so a run that is still
 *                                  going, degraded or failed is shown
 *                                  without holding a run id.
 *
 * The new-engagement wizard (T-0117 / C20-131) drives /run at the end of
 * step 3 (Baseline docs → Continue) and then polls /runs/:run_id from
 * step 4 (AI base formation) until the status is terminal.
 *
 * Note: the worker that actually does the AI work consumes
 * `base_formation.requested` off the bus; the POST returns 202 the moment
 * the run row is inserted, and the GET reflects the worker's progress.
 */

export const RunBaseFormationResultSchema = z.object({
  run_id: z.string().uuid(),
  status: z.string(),
});
export type RunBaseFormationResult = z.infer<typeof RunBaseFormationResultSchema>;

export interface RunBaseFormationInput {
  trigger_source?: 'manual';
}

/**
 * The structured account behind `failure_reason`: the code, which quota the
 * AI service ran out of and how long it asked us to wait, sections read over
 * built, and the sections that failed. While a run is still going it holds
 * the last rate-limit notice the worker saw.
 */
export const BaseFormationFailureDetailSchema = z.object({
  code: z.string(),
  limit: z.enum(['tokens_per_minute', 'requests_per_minute', 'unknown']).nullable(),
  retry_after_seconds: z.number().nullable(),
  windows_done: z.number().int().nonnegative(),
  windows_total: z.number().int().nonnegative().nullable(),
  failed_windows: z.array(z.object({ window_index: z.number().int(), error_code: z.string() })),
});
export type BaseFormationFailureDetail = z.infer<typeof BaseFormationFailureDetailSchema>;

export const BaseFormationRunStatusSchema = z.object({
  run_id: z.string().uuid(),
  status: z.string(),
  proposal_count: z.number().int().nonnegative(),
  low_confidence_count: z.number().int().nonnegative(),
  low_confidence_threshold: z.number(),
  trigger_source: z.string(),
  failure_reason: z.string().nullable(),
  token_cost_cents: z.number().int().nonnegative(),
  started_at: z.string().nullable(),
  finished_at: z.string().nullable(),
  created_at: z.string(),
  /**
   * T-0159: sections of the baseline documents read by the AI over the
   * sections built — real counts, null until the sections are built. Absent
   * from older servers.
   */
  progress: z
    .object({
      done: z.number().int().nonnegative(),
      total: z.number().int().nonnegative(),
      unit: z.literal('sections'),
    })
    .nullable()
    .optional(),
  /** Absent from older servers. */
  failure_detail: BaseFormationFailureDetailSchema.nullable().optional(),
  heartbeat_at: z.string().nullable().optional(),
});
export type BaseFormationRunStatus = z.infer<typeof BaseFormationRunStatusSchema>;

export const LatestBaseFormationRunSchema = z.object({
  run: BaseFormationRunStatusSchema.nullable(),
});

/**
 * POST /api/v1/engagements/:engagement_id/base-formation/run
 *
 * Manual trigger. The wizard mints a stable Idempotency-Key per open so a
 * double-click on Continue doesn't queue two runs. The "one-in-flight"
 * structural guard on the backend also collapses concurrent triggers, so
 * this is belt-and-braces.
 */
export function runBaseFormation(
  engagementId: string,
  input: RunBaseFormationInput = { trigger_source: 'manual' },
  idempotencyKey?: string,
): Promise<RunBaseFormationResult> {
  return apiClient(
    `/api/v1/engagements/${encodeURIComponent(engagementId)}/base-formation/run`,
    RunBaseFormationResultSchema,
    {
      method: 'POST',
      body: input,
      ...(idempotencyKey ? { idempotencyKey } : {}),
    },
  );
}

export function getBaseFormationRun(
  engagementId: string,
  runId: string,
): Promise<BaseFormationRunStatus> {
  return apiClient(
    `/api/v1/engagements/${encodeURIComponent(engagementId)}/base-formation/runs/${encodeURIComponent(
      runId,
    )}`,
    BaseFormationRunStatusSchema,
  );
}

export function getLatestBaseFormationRun(
  engagementId: string,
): Promise<BaseFormationRunStatus | null> {
  return apiClient(
    `/api/v1/engagements/${encodeURIComponent(engagementId)}/base-formation/runs/latest`,
    LatestBaseFormationRunSchema,
  ).then((r) => r.run);
}

export function isRunInFlight(status: string | undefined): boolean {
  return status === 'queued' || status === 'running';
}

/**
 * Start a run. Every call mints its own Idempotency-Key: a retry after a
 * failed or degraded run must create a new run, not replay the old one.
 * On success the latest-run query is primed with the new run so the screen
 * shows it at once.
 */
export function useBaseFormationRunMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      engagementId,
      idempotencyKey,
    }: {
      engagementId: string;
      idempotencyKey?: string;
    }) =>
      runBaseFormation(
        engagementId,
        { trigger_source: 'manual' },
        idempotencyKey ?? crypto.randomUUID(),
      ),
    onSuccess: (_result, vars) => {
      void qc.invalidateQueries({ queryKey: ['base-formation', 'latest', vars.engagementId] });
    },
  });
}

/**
 * The engagement's newest run, polled every 3s while it is queued or
 * running. The documents screen's banner and "Run AI extraction" button
 * read this instead of remembering a run id, so a run that is already going,
 * or ended degraded or failed, is shown after a reload too.
 */
export function useLatestBaseFormationRunQuery(engagementId: string | undefined) {
  return useQuery({
    queryKey: ['base-formation', 'latest', engagementId] as const,
    queryFn: () => getLatestBaseFormationRun(engagementId!),
    enabled: Boolean(engagementId),
    refetchInterval: (q) => (isRunInFlight(q.state.data?.status) ? 3000 : false),
  });
}

/** `ai.no_documents`: the run found no indexed document to read (T-0228). */
export const NO_DOCUMENTS_MESSAGE =
  'Nothing to read yet: no document has been indexed for this engagement. Upload a document; extraction starts on its own once it is indexed.';

/**
 * What to tell the user about a run, in plain words. Null when there is
 * nothing to say (queued / running with no rate-limit notice, succeeded).
 *
 *   - running + a rate-limit notice: the AI service is busy, which quota,
 *     and roughly how long until it retries;
 *   - degraded: how many sections were read and that the rest were
 *     skipped, with the rate limit named when that is why;
 *   - failed with `ai.no_documents` (tone `empty`): there was nothing to
 *     read, and what makes a run start;
 *   - failed: the reason, in words, and that running again starts over.
 */
export function describeBaseFormationRun(
  run: Pick<BaseFormationRunStatus, 'status' | 'failure_reason' | 'failure_detail' | 'progress'>,
): { tone: 'busy' | 'degraded' | 'failed' | 'empty'; message: string } | null {
  const detail = run.failure_detail ?? null;
  const code = detail?.code ?? (run.failure_reason ?? '').replace(/\[.*$/, '');
  if (isRunInFlight(run.status)) {
    if (detail?.code !== 'ai.rate_limited') return null;
    const wait = detail.retry_after_seconds;
    return {
      tone: 'busy',
      message:
        `The AI service is busy (${quotaWords(detail.limit)}). It will retry on its own` +
        (wait !== null && wait > 0 ? `; try again in about ${wait} seconds.` : '.'),
    };
  }
  if (run.status === 'degraded') {
    const read = detail?.windows_done ?? run.progress?.done ?? null;
    const total = detail?.windows_total ?? run.progress?.total ?? null;
    const count =
      read !== null && total !== null
        ? `Read ${read} of ${total} section${total === 1 ? '' : 's'}; the rest were skipped`
        : 'Some sections were skipped';
    const why =
      code === 'ai.rate_limited'
        ? ' after repeated rate limits.'
        : code === 'ai.parse_failed'
          ? ' because the AI answer could not be read.'
          : code === 'ai.vector_unavailable'
            ? '; the search index could not be reached.'
            : '.';
    return { tone: 'degraded', message: `${count}${why} Run again to read the rest.` };
  }
  if (run.status === 'failed') {
    if (code === 'ai.no_documents') return { tone: 'empty', message: NO_DOCUMENTS_MESSAGE };
    return { tone: 'failed', message: failureWords(code) };
  }
  return null;
}

function quotaWords(limit: BaseFormationFailureDetail['limit']): string {
  if (limit === 'tokens_per_minute') return 'tokens per minute';
  if (limit === 'requests_per_minute') return 'requests per minute';
  return 'rate limit';
}

function failureWords(code: string): string {
  switch (code) {
    case 'ai.budget_exhausted':
      return "This engagement's AI budget is used up. Raise the cap under Settings before running again.";
    case 'ai.worker_stalled':
    case 'ai.handoff_failed':
      return 'The extraction stopped responding and was closed. Run again to start over.';
    case 'ai.rate_limited':
      return 'The AI service was busy for too long and the run was closed. Run again to start over.';
    case 'ai.persist_failed':
      return 'The proposals could not be saved. Run again to start over.';
    default:
      return `Extraction failed (${code || 'unknown reason'}). Run again to start over.`;
  }
}

/**
 * Polling hook used by the wizard's step 4 — refetches every 2s while
 * the run is in `queued`/`running` and stops once terminal. Disabled
 * until both an engagement id + run id are known.
 */
export function useBaseFormationRunQuery(
  engagementId: string | undefined,
  runId: string | undefined,
) {
  return useQuery({
    queryKey: ['base-formation', 'run', engagementId, runId] as const,
    queryFn: () => getBaseFormationRun(engagementId!, runId!),
    enabled: Boolean(engagementId) && Boolean(runId),
    refetchInterval: (q) => {
      const s = q.state.data?.status;
      // 'degraded' is terminal too (the run stopped with partial output).
      if (s === 'succeeded' || s === 'failed' || s === 'degraded') return false;
      return 2000;
    },
  });
}
